import React, { useState, useEffect, useMemo, useCallback, forwardRef, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Backdrop,
  Box, 
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  Avatar,
  Grid,
  IconButton,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Draggable from 'react-draggable';
import { DataGridPro, GridActionsCellItem, koKR } from '@mui/x-data-grid-pro';
import  {
  Add,
  ContentPaste,
  Delete,
  Edit,
  MoreVert,
  Search,
  OpenInNew,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { debounce } from 'lodash'; 
import { ConfirmDialog, DialogTitleClose, PaperComponent, } from "../dialog";
import { CustomNoRowsOverlay, } from "../datagrid";
import { FormInputText } from "../form";
import AddressBookDialogRegister from './AddressBookDialogRegister';
import * as addressBookActions from '../../store/addressBook';
import { getGridHeight } from '../../constants/gridHeights';
import { formatMobileNumber } from '../../utils/formatters';

const defaultValues = {
  searchWord: ""
};

const ALL_INITIAL = { label: '전체', value: '전체' };
const KOREAN_INITIALS = ['ㄱ', 'ㄴ', 'ㄷ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅅ', 'ㅇ', 'ㅈ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ']
  .map((value) => ({ label: value, value }));
const ENGLISH_INITIALS = [
  { label: 'A–E', value: 'A-E' },
  { label: 'F–J', value: 'F-J' },
  { label: 'K–O', value: 'K-O' },
  { label: 'P–T', value: 'P-T' },
  { label: 'U–Z', value: 'U-Z' },
];

// 현재 화면에 맞는 Grid 높이 계산
const GRID_HEIGHT = getGridHeight('TWO_LINE_LAYER', 0); 

const AddressBookDialog = ({ open, setOpen, type, credential, email }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {handleSubmit, reset, control, getValues, setValue } = useForm({ defaultValues: defaultValues });
  const [crudMode, setCrudMode] = useState('');
  const [loaded, setLoaded] = useState(true);
  const [pageSize, setPageSize] = useState(100);
  const [page, setPage] = useState(0);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [errors, setErrors] = useState([]);
  const [publicYN, setPublicYN] = useState({});
  const [checked, setChecked] = useState(false);
  const [sort, setSort] = useState([]);
  const [openRegisterDialog, setOpenRegisterDialog] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [actionMenu, setActionMenu] = useState(null);
  const [initialFilter, setInitialFilter] = useState('전체');
  const [script, setScript] = useState('ko');
  const searchInputRef = useRef(null);
  const isMobileRef = useRef(false);
  isMobileRef.current = isMobile;

  const focusSearch = () => {
    if (!isMobileRef.current) searchInputRef.current?.focus();
  };

  const dispatch = useDispatch();

  const selectAll = (searchWord, sort, initConst) => dispatch(addressBookActions.selectAll(searchWord, sort, initConst));
  const selectAddressBook = (seq) => dispatch(addressBookActions.select(seq));
  const createAddressBook = (data) => dispatch(addressBookActions.create(data));
  const modifyAddressBook = (data) => dispatch(addressBookActions.modify(data));
  const removeAddressBook = (seq) => dispatch(addressBookActions.remove(seq));

  const rows = useSelector((state) => state.addressBook.addressBooks);
  const rowCount = useSelector((state) => state.addressBook.count);
  const selectedRow = useSelector((state) => state.addressBook.addressBook);
  
  const initialButtons = [ALL_INITIAL, ...(script === 'en' ? ENGLISH_INITIALS : KOREAN_INITIALS)];
  const buttonStyle = {
    minWidth: '15px',        // 버튼의 최소 너비를 줄입니다
    //padding: '1px 5.5px',  // 한글 + 영문
    padding: '1px 22.5px',   // 한글
    fontSize: '0.7rem',      // 글자 크기를 줄입니다
  };

  // Dialog가 열릴 때마다 검색어 입력 필드에 포커스
  useEffect(() => {
    if (open) {
      setScript('ko');
      setInitialFilter('전체');
      selectAllAddressBooks();
      
      // setTimeout을 사용하여 Dialog가 완전히 열린 후 포커스
      const timer = setTimeout(() => {
        focusSearch();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    if (rows.length > 0) {
      const initialPublicYN = {};
      rows.forEach(row => {
        initialPublicYN[row.seq] = row.publicYN;
      });
      setPublicYN(initialPublicYN);
    }
  }, [rows]);

  // 주소록 목록 조회
  const selectAllAddressBooks = useCallback(() => {
    setLoaded(false);
    dispatch(addressBookActions.selectAll(getValues("searchWord"), sort))
      .then((response) => {
        console.log(response);
        focusSearch(); // 항상 포커스 복원
      })
      .catch(error => {
        console.error('주소록 목록 조회 실패:', error);
        setLoaded(true);
      })
      .finally(() => {
        setLoaded(true);
        focusSearch(); // 항상 포커스 복원
      });
  }, [dispatch, getValues, sort]); 

  // 검색어 검색 함수
  const debouncedSearchFn = useMemo(
    () => debounce((term) => {
      dispatch(addressBookActions.selectAll(term, sort)).finally(() => {
        // 데이터 로딩 완료 후 포커스 복원
        if (searchInputRef.current) {
          focusSearch();
        }
      });
    }, 300),
    [dispatch, sort]
  );
  
  // 검색어 입력 핸들러
  const handleChangeSearchWord = useCallback((e) => {
    const searchWord = e.target.value;
    setValue("searchWord", searchWord);
    debouncedSearchFn(searchWord);
  }, [debouncedSearchFn, setValue]);

  const handleInitialClick = (initial) => {
    setInitialFilter(initial);
    setLoaded(false);
    // "전체"가 아닐 경우에만 dispatch
    const initConst = initial !== "전체" ? initial : null;

    dispatch(addressBookActions.selectAll(getValues("searchWord"), sort, initConst))
    setLoaded(true);
  };

  const handleScriptChange = (next) => {
    if (next === script) return;
    setScript(next);
    setInitialFilter('전체');
    setLoaded(false);
    dispatch(addressBookActions.selectAll(getValues("searchWord"), sort));
    setLoaded(true);
  };


  // 검색버튼 클릭 핸들러
  const handleClickSearch = () => {
    selectAllAddressBooks();
  };

  // 새 주소 추가 다이얼로그 열기
  const handleClickRegister = () => {
    setCrudMode('C');
    setOpenRegisterDialog(true);
  };

  // 주소록 등록 성공 후 주소록 목록 다시 조회
  const handleRegisterSuccess = () => {
    selectAllAddressBooks(); // 주소록 목록 다시 조회
  };
  
  const handleClose = () => {
    setActionMenu(null);
    setOpen(false);
  };

  const handleActionMenuClick = (type) => {
    const row = actionMenu?.row;
    setActionMenu(null);
    if (!row) return;
    handleAction({ type, params: { row } });
  };

  const queryPaging = useMemo(
    () => ({
      page,
      pageSize,
    }), [page, pageSize]
  );

  
  const handlePublicYNChange = useCallback(async (e, params) => {

    e.stopPropagation();

    const newPublicYN = e.target.checked;
    const { id, row } = params;
    const updatedAddressBook = { ...row, publicYN:newPublicYN};

    //e.stopPropagation();
    //console.log('newPublicYN:', newPublicYN,'seq:', id, 'params:', params);
    
    // 로컬 상태 즉시 업데이트
    setPublicYN(prev => ({ ...prev, [row.seq]: newPublicYN }));

    try {
      // 서버에 변경 사항 전송
      await dispatch(addressBookActions.modify(updatedAddressBook));
    } catch (error) {
      console.error('공유 여부 변경 실패:', error);
      // 실패 시 원래 상태로 되돌리기
      setPublicYN(prev => ({ ...prev, [row.seq]: !newPublicYN }));
    }
  }, [dispatch]);

  const handleDeleteConfirm = useCallback(async (seq) => {
    setOpenBackdrop(true);
    try {
      await removeAddressBook(seq);
      selectAllAddressBooks();
    } catch (error) {
      console.error('주소록 삭제 실패:', error);
    } finally {
      setDeleteTarget(null);
      setOpenBackdrop(false);
    }
  }, [removeAddressBook, selectAllAddressBooks]);

  // 주소록 선택 기능 구현
  const handleAction = useCallback(async ({ type, params }) => {
    const seq = params.row.seq;
    //console.log(type, params);

    if (type === 'delete') {
      setDeleteTarget(params.row);
      setConfirmOpen(true);
      return;
    }

    setOpenBackdrop(true);

    switch (type) {
      case 'detail':
      case 'edit':
      case 'paste':
        setCrudMode(type === 'detail' ? 'R' : type === 'edit' ? 'U' : 'C');
        try {
          await selectAddressBook(seq);
          setOpenRegisterDialog(true);
        } catch (error) {
          console.error('주소록 선택 실패:', error);
        }
        break;
      default:
        break; 
    }

    setOpenBackdrop(false);
  }, [selectAddressBook]);

  const generateActions = useCallback((params) => {
    let arrActions = [
      <GridActionsCellItem
        icon={<OpenInNew />}
        label={"상세"}
        onClick={() => handleAction({ type: 'detail', params })}
        showInMenu
      />,
      <GridActionsCellItem
        icon={<Edit />}
        label={"수정"}
        onClick={() => handleAction({ type: 'edit', params })}
        showInMenu
      />,
      <GridActionsCellItem
        icon={<ContentPaste />}
        label={"복사하여 등록"}
        onClick={() => handleAction({ type: 'paste', params })}
        showInMenu
      />,
      <GridActionsCellItem
        icon={<Delete />}
        label={"삭제"}
        onClick={() => handleAction({ type: 'delete', params })}
        showInMenu
      />,
    ];

    return arrActions;
  }, [handleAction]);

  const columns = useMemo(() => 
    [
      { field: 'name', headerName: '이름', flex: 1.5 },
      { field: 'email', headerName: '이메일', flex: 2 },
      { field: 'phone', headerName: '모바일', flex: 1.5,
        renderCell: (params) => formatMobileNumber(params.value)
      },
      { field: 'company', headerName: '거래선명', flex: 2 },
      { field: 'duties', headerName: '직책', flex: 1 },
      { field: 'publicYN', headerName: '공유여부', flex: 1,
        renderCell: (params) => {
          const isPublic = publicYN[params.row.seq] ?? params.value;  
          return (
            <Tooltip title={isPublic ? "공유" : "비공유"} arrow key={`tooltip-${params.row.seq}-${isPublic}`}>
              <Switch
                checked={isPublic}
                onChange={(e) => handlePublicYNChange(e, params)}
                color="primary"
              />
            </Tooltip>
          );
        },
        renderHeader: (params) => (
          <Tooltip title="주소록 공유여부" arrow>
            <span>{params.colDef.headerName}</span>
          </Tooltip>
        ),
      },
      { field: 'actions', 
        headerName: '기능', 
        flex: 0.5, 
        type: 'actions', 
        getActions: (params) => generateActions(params),
        renderHeader: (params) => (
          <Tooltip title="상세/수정/복사하여 등록/삭제" followCursor>
            <Box>기능</Box>
          </Tooltip>
        ),
      },
    ], [generateActions, publicYN, handlePublicYNChange]
  );  
  
  const PaperComponent = forwardRef((props, ref) => {
    const nodeRef = useRef(null);
    return (
      <Draggable
        handle="#draggable-dialog-title"
        cancel={'[class*="MuiDialogContent-root"]'}
        nodeRef={nodeRef}
      >
        <Paper {...props} ref={nodeRef}>
          {props.children}
        </Paper>
      </Draggable>
    );
  });
  
  
  return (
    <>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openBackdrop}
        // onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Dialog
        open={open}
        onClose={handleClose}
        PaperComponent={isMobile ? undefined : PaperComponent}
        PaperProps={isMobile ? { sx: { display: 'flex', flexDirection: 'column' } } : undefined}
        fullScreen={isMobile}
        aria-labelledby="draggable-dialog-title"
        fullWidth={!isMobile}
        maxWidth={isMobile ? false : 'md'}
      >
        <DialogTitleClose
          id="draggable-dialog-title"
          onClose={handleClose}
          style={isMobile ? undefined : { cursor: 'move' }}
        >
          {"주소록"}
        </DialogTitleClose>
        <DialogContent sx={isMobile ? { flex: '1 1 auto', minHeight: 0 } : undefined}>
          <Box sx={{ mt: 0 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <FormInputText
                  name={"searchWord"}
                  control={control}
                  label={"검색어(회사명/이름/이메일/모바일)를 입력하세요."}
                  onCustomChange={handleChangeSearchWord}
                  inputRef={searchInputRef}
                  inputProps={{             
                    autoComplete: 'off'     // 자동완성 비활성화
                  }}
                />
              </Grid>
              {!isMobile && (
                <Grid item xs={12} sm={6} container justifyContent="flex-end">
                  <Button
                    variant="contained"
                    startIcon={<Search />}
                    onClick={handleClickSearch}
                    sx={{ mr: 1 }}
                  >
                    {"검색"}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleClickRegister}
                  >
                    {"등록하기"}
                  </Button>
                </Grid>
              )}
              <Grid item xs={12}>
                {isMobile ? (
                  <Box sx={{ width: '100%' }}>
                    <Tabs
                      value={script}
                      onChange={(_, value) => handleScriptChange(value)}
                      aria-label="한글 영문 전환"
                      sx={{
                        minHeight: 40,
                        '& .MuiTab-root': { minHeight: 40, minWidth: 72, py: 0 },
                      }}
                    >
                      <Tab value="ko" label="한글" />
                      <Tab value="en" label="영문" />
                    </Tabs>
                    <Box sx={{ display: 'flex', overflowX: 'auto', width: '100%' }}>
                      {initialButtons.map((initial) => {
                        const selected = initialFilter === initial.value;
                        return (
                          <Button
                            key={initial.value}
                            onClick={() => handleInitialClick(initial.value)}
                            variant="text"
                            disableRipple
                            sx={{
                              flex: '0 0 auto',
                              minWidth: 40,
                              height: 40,
                              px: 1,
                              borderRadius: 0,
                              color: selected ? 'primary.main' : 'text.secondary',
                              fontWeight: selected ? 700 : 400,
                              borderBottom: '2px solid',
                              borderColor: selected ? 'primary.main' : 'transparent',
                              WebkitTapHighlightColor: 'transparent',
                              '&:hover, &.Mui-focusVisible': { backgroundColor: 'transparent' },
                            }}
                          >
                            {initial.label}
                          </Button>
                        );
                      })}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={script}
                      onChange={(_, value) => { if (value) handleScriptChange(value); }}
                      aria-label="한글 영문 전환"
                    >
                      <ToggleButton value="ko">한글</ToggleButton>
                      <ToggleButton value="en">영문</ToggleButton>
                    </ToggleButtonGroup>
                    <ButtonGroup variant="outlined" size="small" aria-label="초성 검색">
                      {initialButtons.map((initial) => (
                        <Button
                          key={initial.value}
                          onClick={() => handleInitialClick(initial.value)}
                          variant={initialFilter === initial.value ? "contained" : "outlined"}
                          style={buttonStyle}
                        >
                          {initial.label}
                        </Button>
                      ))}
                    </ButtonGroup>
                  </Box>
                )}
              </Grid>
            </Grid>
            {isMobile ? (
              <Box sx={{ mt: 2 }}>
                {!loaded && <LinearProgress />}
                {loaded && rows.length === 0 && (
                  <Typography sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                    {"조회된 데이터가 없습니다."}
                  </Typography>
                )}
                {rows.map((row, index) => {
                  const detail = [formatMobileNumber(row.phone), row.company, row.duties].filter(Boolean).join(' · ');
                  const initial = (row.name || '').trim().charAt(0) || '?';
                  return (
                    <Box
                      key={row.seq}
                      onClick={() => handleAction({ type: 'edit', params: { row } })}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        py: 1.25,
                        borderBottom: index < rows.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          fontSize: '1rem',
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {initial}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, lineHeight: 1.3 }} noWrap>{row.name}</Typography>
                        <Typography variant="body2" noWrap sx={{ lineHeight: 1.3 }}>{row.email}</Typography>
                        {detail && (
                          <Typography variant="caption" color="text.secondary" noWrap component="p" sx={{ m: 0, lineHeight: 1.3 }}>
                            {detail}
                          </Typography>
                        )}
                      </Box>
                      <Box
                        onClick={(event) => event.stopPropagation()}
                        sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
                      >
                        <Typography variant="caption" color="text.secondary">{"공유"}</Typography>
                        <Switch
                          size="small"
                          checked={publicYN[row.seq] ?? row.publicYN}
                          onChange={(event) => handlePublicYNChange(event, { id: row.seq, row })}
                        />
                      </Box>
                      <IconButton
                        aria-label="기능"
                        edge="end"
                        onClick={(event) => {
                          event.stopPropagation();
                          setActionMenu({ anchor: event.currentTarget, row });
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
            ) : (
            <div style={{ height: GRID_HEIGHT.FULL, width: '100%', marginTop: '20px' }}>
              <DataGridPro
                localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
                sx={{ cursor: 'pointer', fontSize: '0.85em' }}
                initialState={{ pinnedColumns: { right: ['actions'] } }}
                slots={{
                  noRowsOverlay: CustomNoRowsOverlay,
                  loadingOverlay: LinearProgress,
                }}
                headerHeight={38}
                rowHeight={34}
                getRowId={(row) => row.seq} // seq를 고유 ID로 사용
                rows={rows}
                rowCount={rowCount}
                loading={!loaded}
                columns={columns}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={[10, 20, 50, 100]}
                pagination
                onRowDoubleClick={(params) => handleAction({ type: 'edit', params })}
              />
            </div>
            )}
          </Box>
        </DialogContent>
        <DialogActions {...(isMobile ? {
          sx: {
            '&&': {
              justifyContent: 'stretch',
              gap: '12px',
              padding: '12px 16px calc(12px + env(safe-area-inset-bottom)) !important',
            },
            '& .MuiButton-root': {
              flex: 1,
              height: 48,
              marginLeft: '0 !important',
              fontSize: '1rem',
            },
          },
        } : {})}>
          <Button variant={isMobile ? 'outlined' : 'text'} onClick={handleClose}>닫기</Button>
          {isMobile && (
            <Button variant="contained" startIcon={<Add />} onClick={handleClickRegister}>등록하기</Button>
          )}
        </DialogActions>
        <Menu
          anchorEl={actionMenu?.anchor}
          open={Boolean(actionMenu)}
          onClose={() => setActionMenu(null)}
        >
          <MenuItem onClick={() => handleActionMenuClick('detail')}>
            <ListItemIcon><OpenInNew fontSize="small" /></ListItemIcon>
            <ListItemText>상세</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleActionMenuClick('paste')}>
            <ListItemIcon><ContentPaste fontSize="small" /></ListItemIcon>
            <ListItemText>복사하여 등록</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleActionMenuClick('delete')}>
            <ListItemIcon><Delete fontSize="small" /></ListItemIcon>
            <ListItemText>삭제</ListItemText>
          </MenuItem>
        </Menu>
      </Dialog>
      <ConfirmDialog
        removeId={deleteTarget?.seq}
        title="삭제"
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      >
        {`"${deleteTarget?.name || ""}" 연락처를 삭제하시겠습니까?`}
      </ConfirmDialog>
      <AddressBookDialogRegister
        crudMode={crudMode}
        setCrudMode={setCrudMode}
        open={openRegisterDialog}
        setOpen={setOpenRegisterDialog}
        selectedRow={selectedRow}
        queryPaging={queryPaging}
        onSuccess={handleRegisterSuccess}
      />
    </>
  );
};

export default AddressBookDialog;